/**
 * Lightweight, render-side particle/effect system: combat tracers + hit
 * flashes, death debris, serve motes, anomaly smoke, construction dust, and
 * phase-advance confetti.
 *
 * Purely cosmetic and purely reactive — it never touches sim state. Each
 * frame it diffs the current GameState against a small cache of the previous
 * frame's entities (hp/pos/buildTicks/anomalyTicks/serving) to notice things
 * like "this unit just took damage" or "this building just finished," and it
 * reads state.events once per sim tick for discrete moments like
 * phaseAdvanced. Effects are pooled with a hard cap so a big battle can't
 * tank frame rate — spawns beyond the cap are silently dropped.
 */

import { BUILDINGS, UNITS, entityCenter } from '../sim';
import type { Entity, EntityId, GameState, PlayerIdx } from '../sim';
import type { Camera } from './camera';
import { effectiveTileSize, worldToScreen } from './camera';
import { PLAYER_COLORS } from './sprites';

const CAP = 200;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  gravity: number;
}

type Effect =
  | { kind: 'tracer'; born: number; life: number; x0: number; y0: number; x1: number; y1: number; color: string }
  | { kind: 'hitFlash'; born: number; life: number; x: number; y: number; radius: number }
  | { kind: 'debris'; born: number; life: number; particles: Particle[] }
  | { kind: 'serveMote'; born: number; life: number; x: number; y: number }
  | { kind: 'anomalySmoke'; born: number; life: number; x: number; y: number; vx: number; vy: number; color: string }
  | { kind: 'constructionDust'; born: number; life: number; x: number; y: number; maxRadius: number }
  | { kind: 'confetti'; born: number; life: number; particles: Particle[] };

interface EntitySnapshot {
  owner: PlayerIdx;
  kind: Entity['kind'];
  hp: number;
  x: number;
  y: number;
  buildTicks?: number;
  anomalyTicks?: number;
  serving?: boolean;
}

/**
 * Mirrors the fog rule drawEntities uses: units need current visibility,
 * everything else (buildings/dataNodes/markets) just needs to be explored.
 * Effects must respect the same rule or they'd leak information through fog.
 */
export type VisibilityFn = (x: number, y: number, kind: Entity['kind']) => boolean;

function entityAttack(e: Entity): { damage: number; range: number; periodSec: number } | undefined {
  if (e.kind === 'unit') return UNITS[e.type].attack;
  if (e.kind === 'building') return BUILDINGS[e.type].attack;
  return undefined;
}

const SERVE_MOTE_PERIOD_MS = 1500;
const ANOMALY_SMOKE_PERIOD_MS = 380;

export class EffectsSystem {
  private effects: Effect[] = [];
  private prev = new Map<EntityId, EntitySnapshot>();
  private lastServeMote = new Map<EntityId, number>();
  private lastAnomalySmoke = new Map<EntityId, number>();
  private lastEventTick = -1;

  private spawn(e: Effect): void {
    if (this.effects.length >= CAP) return;
    this.effects.push(e);
  }

  /** Call once per render frame: diffs state, spawns new effects, prunes dead ones. */
  update(state: GameState, now: number, isVisible: VisibilityFn): void {
    this.detectChanges(state, now, isVisible);
    if (state.tick !== this.lastEventTick) {
      this.lastEventTick = state.tick;
      this.processEvents(state, now, isVisible);
    }
    if (this.effects.length > 0) {
      this.effects = this.effects.filter((e) => now - e.born < e.life);
    }
  }

  private detectChanges(state: GameState, now: number, isVisible: VisibilityFn): void {
    const seen = new Set<EntityId>();
    for (const e of state.entities) {
      seen.add(e.id);
      const c = entityCenter(e);
      const prevSnap = this.prev.get(e.id);
      const snap: EntitySnapshot = {
        owner: e.owner,
        kind: e.kind,
        hp: e.hp,
        x: c.x,
        y: c.y,
        buildTicks: e.kind === 'building' ? e.buildTicks : undefined,
        anomalyTicks: e.kind === 'unit' ? e.anomalyTicks : undefined,
        serving: e.kind === 'unit' ? e.serving : undefined,
      };
      const visibleNow = isVisible(snap.x, snap.y, e.kind);

      if (prevSnap && visibleNow) {
        if (snap.hp < prevSnap.hp) this.spawnHit(state, e, snap, now, isVisible);
        if (prevSnap.buildTicks !== undefined && prevSnap.buildTicks > 0 && snap.buildTicks === 0) {
          this.spawn({
            kind: 'constructionDust',
            born: now,
            life: 500,
            x: snap.x,
            y: snap.y,
            maxRadius: e.kind === 'building' ? BUILDINGS[e.type].size * 0.7 : 1.5,
          });
        }
      }

      if (visibleNow && snap.anomalyTicks && snap.anomalyTicks > 0) {
        const last = this.lastAnomalySmoke.get(e.id) ?? 0;
        if (now - last > ANOMALY_SMOKE_PERIOD_MS) {
          this.lastAnomalySmoke.set(e.id, now);
          this.spawn({
            kind: 'anomalySmoke',
            born: now,
            life: 900,
            x: snap.x + (Math.random() - 0.5) * 0.6,
            y: snap.y - 0.3,
            vx: (Math.random() - 0.5) * 0.3,
            vy: -0.5 - Math.random() * 0.3,
            color: Math.random() < 0.5 ? '#8b98ad' : '#ff5c5c',
          });
        }
      } else {
        this.lastAnomalySmoke.delete(e.id);
      }

      if (visibleNow && snap.serving) {
        const last = this.lastServeMote.get(e.id);
        if (last === undefined) {
          // stagger the first mote so agents don't all pulse in lockstep
          this.lastServeMote.set(e.id, now - (e.id % SERVE_MOTE_PERIOD_MS));
        } else if (now - last > SERVE_MOTE_PERIOD_MS) {
          this.lastServeMote.set(e.id, now);
          this.spawn({ kind: 'serveMote', born: now, life: 900, x: snap.x, y: snap.y - 0.6 });
        }
      } else {
        this.lastServeMote.delete(e.id);
      }

      this.prev.set(e.id, snap);
    }

    for (const [id, snap] of this.prev) {
      if (seen.has(id)) continue;
      if (isVisible(snap.x, snap.y, snap.kind)) this.spawnDeath(snap, now);
      this.prev.delete(id);
      this.lastServeMote.delete(id);
      this.lastAnomalySmoke.delete(id);
    }
  }

  private spawnHit(state: GameState, victim: Entity, snap: EntitySnapshot, now: number, isVisible: VisibilityFn): void {
    this.spawn({
      kind: 'hitFlash',
      born: now,
      life: 260,
      x: snap.x,
      y: snap.y,
      radius: victim.kind === 'building' ? BUILDINGS[victim.type].size * 0.6 : 0.6,
    });

    // Heuristic attacker lookup: nearest hostile entity with an attack stat
    // whose range plausibly reaches the victim. Good enough for a tracer.
    let best: Entity | undefined;
    let bestDist = Infinity;
    for (const e of state.entities) {
      if (e.owner === victim.owner || e.owner === -1) continue;
      const atk = entityAttack(e);
      if (!atk) continue;
      const c = entityCenter(e);
      const d = Math.hypot(c.x - snap.x, c.y - snap.y);
      if (d > atk.range + 1.5) continue;
      if (d < bestDist) {
        bestDist = d;
        best = e;
      }
    }
    if (best && isVisible(entityCenter(best).x, entityCenter(best).y, best.kind)) {
      const c = entityCenter(best);
      const color = best.owner >= 0 ? PLAYER_COLORS[best.owner % PLAYER_COLORS.length] : '#e5484d';
      this.spawn({ kind: 'tracer', born: now, life: 150, x0: c.x, y0: c.y, x1: snap.x, y1: snap.y, color });
    }
  }

  private spawnDeath(snap: EntitySnapshot, now: number): void {
    const color = snap.owner >= 0 ? PLAYER_COLORS[snap.owner % PLAYER_COLORS.length] : '#8b98ad';
    const n = 6 + Math.floor(Math.random() * 3);
    const particles: Particle[] = [];
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
      const speed = 0.8 + Math.random() * 1.4;
      particles.push({
        x: snap.x,
        y: snap.y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed - 0.6,
        color,
        gravity: 2.2,
      });
    }
    this.spawn({ kind: 'debris', born: now, life: 650, particles });
  }

  private processEvents(state: GameState, now: number, isVisible: VisibilityFn): void {
    for (const ev of state.events) {
      if (ev.type === 'phaseAdvanced') this.spawnConfetti(state, ev.player, now, isVisible);
    }
  }

  private spawnConfetti(state: GameState, player: PlayerIdx, now: number, isVisible: VisibilityFn): void {
    const hq = state.entities.find((e) => e.kind === 'building' && e.owner === player && e.type === 'hq');
    if (!hq) return;
    const c = entityCenter(hq);
    if (!isVisible(c.x, c.y, 'building')) return;
    const colors = ['#eab308', '#facc15', '#fde68a'];
    const n = 26;
    const particles: Particle[] = [];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 2.2;
      particles.push({
        x: c.x + (Math.random() - 0.5) * 2,
        y: c.y - 2 - Math.random() * 2,
        vx: Math.cos(a) * speed * 0.6,
        vy: -1.5 - Math.random() * 1.5,
        color: colors[i % colors.length],
        gravity: 2.6,
      });
    }
    this.spawn({ kind: 'confetti', born: now, life: 1400, particles });
  }

  /** Draw all live effects, in world space, respecting camera pan/zoom. */
  draw(ctx: CanvasRenderingContext2D, cam: Camera, now: number): void {
    if (this.effects.length === 0) return;
    const ts = effectiveTileSize(cam);
    const px = Math.max(1, Math.round(ts / 16));

    for (const e of this.effects) {
      const age = now - e.born;
      const t = Math.max(0, Math.min(1, age / e.life));
      switch (e.kind) {
        case 'tracer': {
          const p0 = worldToScreen(cam, e.x0, e.y0);
          const p1 = worldToScreen(cam, e.x1, e.y1);
          ctx.globalAlpha = 1 - t;
          ctx.fillStyle = e.color;
          const steps = 4;
          for (let i = 1; i <= steps; i++) {
            const f = i / (steps + 1);
            const x = p0.x + (p1.x - p0.x) * f;
            const y = p0.y + (p1.y - p0.y) * f;
            ctx.fillRect(x - px * 0.75, y - px * 0.75, px * 1.5, px * 1.5);
          }
          ctx.globalAlpha = 1;
          break;
        }
        case 'hitFlash': {
          if (Math.floor(age / 65) % 2 !== 0) break;
          const p = worldToScreen(cam, e.x, e.y);
          const r = e.radius * ts;
          ctx.globalAlpha = 0.55 * (1 - t);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
          ctx.globalAlpha = 1;
          break;
        }
        case 'debris':
        case 'confetti': {
          const sec = age / 1000;
          ctx.globalAlpha = 1 - t;
          for (const part of e.particles) {
            const x = part.x + part.vx * sec;
            const y = part.y + part.vy * sec + 0.5 * part.gravity * sec * sec;
            const p = worldToScreen(cam, x, y);
            ctx.fillStyle = part.color;
            ctx.fillRect(p.x - px / 2, p.y - px / 2, px, px);
          }
          ctx.globalAlpha = 1;
          break;
        }
        case 'serveMote': {
          const rise = t * 0.9;
          const p = worldToScreen(cam, e.x, e.y - rise);
          ctx.globalAlpha = 1 - t;
          ctx.fillStyle = '#46e678';
          ctx.font = 'bold 11px monospace';
          ctx.textAlign = 'center';
          ctx.fillText('+$', p.x, p.y);
          ctx.globalAlpha = 1;
          break;
        }
        case 'anomalySmoke': {
          const sec = age / 1000;
          const p = worldToScreen(cam, e.x + e.vx * sec, e.y + e.vy * sec);
          ctx.globalAlpha = 0.7 * (1 - t);
          ctx.fillStyle = e.color;
          ctx.fillRect(p.x - px / 2, p.y - px / 2, px, px);
          ctx.globalAlpha = 1;
          break;
        }
        case 'constructionDust': {
          const r = e.maxRadius * ts * t;
          const p = worldToScreen(cam, e.x, e.y);
          ctx.globalAlpha = 0.6 * (1 - t);
          ctx.fillStyle = '#eab308';
          const points = 8;
          for (let i = 0; i < points; i++) {
            const a = (Math.PI * 2 * i) / points;
            const x = p.x + Math.cos(a) * r;
            const y = p.y + Math.sin(a) * r * 0.6;
            ctx.fillRect(x - px / 2, y - px / 2, px, px);
          }
          ctx.globalAlpha = 1;
          break;
        }
      }
    }
  }
}
